import {
  QuestionType,
  type ReviewPageBasicQuestion,
  type ReviewPageCompetency,
} from "./questions";
import {
  DerivedScreenerStatus,
  ScreenerStatus,
  ScreenerType,
} from "@/types/screeners";
import type { MakeRequired, Nullable, NullableValues } from "@/types/utils";

export enum ScreenerBuilderStep {
  Position = "position",
  Context = "context",
  Locations = "locations",
  Review = "review",
}

export enum SeniorityLevel {
  EntryLevel = "Entry Level",
  JuniorLevel = "Junior Level",
  MidLevel = "Mid Level",
  SeniorLevel = "Senior Level",
  ExecutiveLevel = "Executive Level",
}

export enum GenerationStatus {
  New = "new",
  Generating = "generating",
  Complete = "complete",
  Error = "error",
}

export enum AvailableLanguages {
  English = "en-US",
  Spanish = "es-MX",
  French = "fr-FR",
  Creole = "ht-HT",
}

// ===== Screener Builder Pages =====

export interface SlimScreenerBuilderDetails {
  createdAt: string;
  deletedAt: Nullable<string>;
  derivedStatus: DerivedScreenerStatus;
  generationStatus: GenerationStatus;
  id: string;
  internalName: string;
  jobTitle: string;
  status: ScreenerStatus;
  type: ScreenerType;
  versionNumber: number;
}

export interface ScreenerBuilderPosition {
  internalName: Nullable<string>;
  isHighTurnoverRole: Nullable<boolean>;
  jobDescription: string;
  jobTitle: string;
  seniorityLevel: Nullable<SeniorityLevel>;
}

export type ScreenerBuilderContext = NullableValues<{
  companyMission: string;
  companyValues: string;
  interviewGuide: string;
  otherNotes: string;
  toneOfVoiceExample: string;
}>;

// Scoring scale was previously a way for users to determine how harshly a
// screener should be stored. That functionality has been removed, but the API
// still requires a scoringScale property so we pass a static default.
export const SCORING_SCALE = 4 as const;

// This interface is currently only used for POST to the screener-builder
// endpoint. All review specific data fetched from the server is retrieved via
// the review-page endpoint.
export interface ScreenerBuilderReviewPostBody {
  // Basic questions and competencies have their content updated via model
  // specific endpoints. The data passed through here is a list of root ID's,
  // used to set their order.
  basicQuestionsOrder: string[];
  competenciesOrder: string[];
  conclusion: string;
  introduction: string;
  languages: AvailableLanguages[];
  scoringScale: typeof SCORING_SCALE;
  voiceQuestionsOrder: string[];
}

// ===== API Interfaces =====
export interface ScreenerBuilderResponse {
  data: {
    // Locations and Review are intentionally excluded here
    // Locations operate under a data model independent of the screener
    // Review has been migrated to a stand-alone endpoint (as we plan to do with
    // the remaining steps)
    context: ScreenerBuilderContext;
    position: ScreenerBuilderPosition;
  };
  generationStatus: GenerationStatus;
  published: boolean;
  screenerId: string;
  step: ScreenerBuilderStep;
  isLegacyScreener?: boolean;
}

type WithProgressStep<T extends object> = T & { progressStep: boolean };

export type ScreenerBuilderDataMap = {
  [ScreenerBuilderStep.Position]: WithProgressStep<ScreenerBuilderPosition>;
  [ScreenerBuilderStep.Context]: WithProgressStep<ScreenerBuilderContext>;
  // Locations are a special case in that their data is a separate model from
  // the core screener. Locations are updated independently (see
  // screener-locations.queries.ts), in the context of locations this endpoint
  // is only ever used to indicate that the user is progressing to the next
  // step.
  [ScreenerBuilderStep.Locations]: undefined;
  [ScreenerBuilderStep.Review]: ScreenerBuilderReviewPostBody;
};

export interface ScreenerBuilderPublishResponse {
  failed: {
    location: string;
    error: string;
  }[];
  message: string;
  successful: {
    location: string;
    url: string;
  }[];
}

export interface PublishedScreenerBuilderUpdateBody {
  internalName?: string;
  jobTitle?: string;
  openingStatement?: string;
  closingStatement?: string;
}

export enum EditorModes {
  Editor = "editor",
  Preview = "preview",
}

// All other pages will have the ID, position is the only page that needs to
// handle a draft with no ID
export type BuilderInfoFirstDraft = Omit<
  ScreenerBuilderResponse,
  "screenerId"
> & { screenerId?: string };

export interface ScreenerBuilderReviewPageResponse {
  // This property is currently only used for legacy CSV screeners. For AI
  // builder screeners it includes all competency questions (including
  // unselected questions) in a flat array.
  assessmentQuestions: ReviewPageBasicQuestion[];
  basicQuestions: ReviewPageBasicQuestion[];
  competencies: ReviewPageCompetency[];
  conclusion: string;
  introduction: string;
  languages?: AvailableLanguages[];
  // WARNING: This is the ID for the individual version of the current screener,
  // it is _not_ the root ID that we typically work with. This ID will change
  // as new versions are created/discarded
  screenerId: string;
  screenerStatus: ScreenerStatus;
  screenerVersionNumber?: number;
  voiceQuestions: ReviewPageBasicQuestion<QuestionType.Voice>[];
}

// If languages are not provided by the API, we set a default of
// [AvailableLanguages.English] in the query.
export type ScreenerBuilderReview = MakeRequired<
  ScreenerBuilderReviewPageResponse,
  "languages"
>;
